"use client";

import dynamic from "next/dynamic";
import { ComponentType, FC, useCallback, useRef, useState, useMemo } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import BFLImage from "@repo/shared-ui/BFLImage";
import Conditional from "@repo/shared-ui/Conditional";
import BFLModal from "@repo/shared-ui/BFLModal";
import SwiperButton from "@repo/shared-ui/SwiperButton";
import useMediaQuery from "@repo/shared-ui/useMediaQuery";
import { VideoModalProps } from "./VideoModalProps.interface";
import {
  IVideocarouselContent,
  IVideoContent,
} from "../../interfaces/ITradingMallContentV1.interface";
import { memo } from "react";
import { twMerge } from "tailwind-merge";

const VIDEO_PLAY_ICON_CLASS =
  "icomoon bf-icon-play-icon inline-flex h-[36px] w-[36px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-link-primary text-[36px] leading-[36px] text-white lg:h-[52px] lg:w-[52px] lg:text-[52px] lg:leading-[52px]";

// Lazy load video Modal
const VideoModal: ComponentType<VideoModalProps> = dynamic(
  () => {
    return import("./VideoModal.tsx") as any;
  },
  { ssr: false },
);

export interface VideoCarouselProps {
  content: IVideocarouselContent;
}

// Renderers
const VideoSlide = memo(
  ({
    video,
    onClick,
  }: {
    video: IVideoContent;
    onClick: (id: string) => void;
  }) => (
    <button
      className="w-full text-sm"
      onClick={() => onClick(video["kpoint-id"])}
    >
      <div className="relative block h-[154px] cursor-pointer overflow-hidden rounded border border-gray-300 md:h-[152px]">
        <BFLImage
          className="bg-cover bg-center"
          src={video?.videoLinks}
          alt={video?.title || "Video Carousel Image"}
          fill={true}
          priority={false}
          loading="lazy"
          unoptimized={true}
        />
        <span className="flex-center-center absolute inset-0">
          <i className={VIDEO_PLAY_ICON_CLASS} />
        </span>
      </div>
      <p className="text-black-100 mb-[6.39px] mt-1 line-clamp-3 text-center leading-[17px] md:mb-0">
        {video?.title}
      </p>
    </button>
  ),
);

const VideoCarousel: FC<VideoCarouselProps> = ({ content }) => {
  const { videoLinks, componentTitle, slidesPerViewDesktop, customClass } = content;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedKpointId, setSelectedKpointId] = useState<string | null>(null);
  const videos = Object.values(videoLinks?.[0] || []);

  const isMobile = useMediaQuery("(max-width: 1024px)");

  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const swiperRef = useRef<SwiperType | null>(null);

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const handleBeforeInit = useCallback((swiper: SwiperType) => {
    if (typeof swiper.params.navigation !== "boolean" && swiper.params.navigation) {
      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;
    }
  }, []);

  const handleSwiperUpdate = useCallback((swiper: SwiperType) => {
    swiperRef.current = swiper;
    setIsAtStart(swiper.isBeginning);
    setIsAtEnd(swiper.isEnd);
  }, []);

  const swiperConfig = useMemo(
    () => ({
      modules: [Navigation, A11y],
      spaceBetween: 8,
      slidesPerView: isMobile ? 1.5 : Number(slidesPerViewDesktop) || 3,
      allowTouchMove: true,
      simulateTouch: true,
      touchRatio: 1,
      resistanceRatio: 0.85,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      navigation: !isMobile
        ? {
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }
        : false,
    }),
    [isMobile, slidesPerViewDesktop],
  );

  const handleVideoClick = useCallback((kpointId: string) => {
    setSelectedKpointId(kpointId);
    setIsOpen(true);
  }, []);

  return (
    <section className={twMerge("mb-6", customClass)}>
      <h2 className="mb-[10px] text-[18px] font-medium leading-6">
        {componentTitle}
      </h2>

      <div className="relative">
        {!isMobile && (
          <>
            <SwiperButton
              ref={prevRef}
              direction="prev"
              isDisabled={isAtStart}
              aria-label="Previous videos"
            />
            <SwiperButton
              ref={nextRef}
              direction="next"
              isDisabled={isAtEnd}
              aria-label="Next videos"
            />
          </>
        )}

        <Swiper
          {...swiperConfig}
          onBeforeInit={handleBeforeInit}
          onInit={handleSwiperUpdate}
          onSlideChange={handleSwiperUpdate}
          onResize={handleSwiperUpdate}
          onReachEnd={() => setIsAtEnd(true)}
          onFromEdge={() => setIsAtEnd(false)}
        >
          {videos?.map((video, idx) => (
            <SwiperSlide key={"videoSlide-" + idx}>
              <VideoSlide video={video} onClick={handleVideoClick} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <Conditional if={isOpen}>
        <BFLModal
          isOpen={isOpen}
          title="Video"
          onClose={() => setIsOpen(false)}
          showHeader={false}
          contentClassName="p-0"
        >
          <VideoModal
            isOpen={isOpen}
            selectedKpointId={selectedKpointId}
            onClose={() => setIsOpen(false)}
          />
        </BFLModal>
      </Conditional>
    </section>
  );
};

export default VideoCarousel;

